'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { PhoneInput } from '@/components/ui/phone-input';
import { toast } from '@/lib/toast';
import { submitCallbackRequest } from './actions';

// Matches normalized format: +61 followed by 9 digits starting with 2, 3, 4, 7, or 8
const phoneRegex = /^\+61[2-478]\d{8}$/;

const formSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .regex(phoneRegex, 'Please enter a valid Australian phone number')
});

type FormData = z.infer<typeof formSchema>;

interface CallbackRequestFormProps {
  onSuccess: () => void;
  onBack: () => void;
}

export function CallbackRequestForm({
  onSuccess,
  onBack
}: CallbackRequestFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phoneNumber: ''
    }
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      const result = await submitCallbackRequest(data.phoneNumber);

      if (!result.success) {
        toast.error(result.error || 'Failed to submit callback request');
        return;
      }

      toast.success(
        'Your callback request has been submitted. A member of the Vale team will be in touch shortly.'
      );
      form.reset();
      onSuccess();
    } catch (error) {
      console.error('Error submitting callback request:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-2.5 flex items-center">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={onBack}
          disabled={isSubmitting}
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="sr-only">Back</span>
        </Button>
        <div className="space-y-1">
          <h2
            // VALE-531: `color: '#000'` moved onto the token.
            className="text-foreground"
            style={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '12px',
              letterSpacing: '-0.28px'
            }}
          >
            Request a callback
          </h2>
          <p
            style={{
              color: 'var(--grey-45)',
              fontFamily: 'Inter',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '12px',
              letterSpacing: '-0.24px'
            }}
          >
            Enter your phone number and we'll call you back shortly.
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Your Phone Number</FormLabel>
                <FormControl>
                  <PhoneInput
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={isSubmitting}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Submit Request
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
